import {payoutApiGet} from './api'
import {PayoutDetailResponse} from './types'
import {getChannelName} from './utils'

export interface ResolvedPayoutPlatform {
  connectedPlatformId: string | number
  availablePlatformId: string | number
}

const getPlatformIdentifiers = (platform: any): ResolvedPayoutPlatform | null => {
  const connectedPlatformId = platform?.id
  const availablePlatformId = platform?.platform_info?.id
  if (!connectedPlatformId || !availablePlatformId) return null
  return {connectedPlatformId, availablePlatformId}
}

export const getRowPlatformId = (row: any) =>
  row?.platform_id || row?.platform?.id || row?.channel_id || row?.id_platform

const getComparableNames = (platform: any) =>
  [
    platform?.display_name,
    platform?.name,
    platform?.platform_info?.name,
    platform?.platform_info?.display_name,
  ]
    .map((name) => String(name || '').trim().toLowerCase())
    .filter(Boolean)

export const getCandidatePlatformsForRow = (row: any, connectedPlatforms: any[] = []) => {
  const rowPlatformId = getRowPlatformId(row)
  if (rowPlatformId) {
    const exactMatch = connectedPlatforms.find(
      (platform) => String(platform?.id) === String(rowPlatformId)
    )
    return exactMatch ? [exactMatch] : []
  }

  const rowChannelName = String(getChannelName(row) || row?.channel || '')
    .trim()
    .toLowerCase()
  if (!rowChannelName) return []

  return connectedPlatforms.filter((platform) => {
    const names = getComparableNames(platform)
    return names.some((name) => name === rowChannelName)
  })
}

const amountsMatch = (left?: number | null, right?: number | null) => {
  if (left === null || left === undefined || right === null || right === undefined) {
    return false
  }
  return Math.abs(Number(left) - Number(right)) < 0.01
}

const isDetailMatchForRow = (row: any, detail?: PayoutDetailResponse | null) => {
  const totals = detail?.financial_totals
  const detailRows = detail?.rows || []
  if (!totals) return false

  const totalsMatch =
    amountsMatch(totals.net_deposit, row?.net_deposit) &&
    amountsMatch(totals.total_sales, row?.total_sales)

  const hasDetailData =
    detailRows.length > 0 ||
    (totals.net_deposit != null && Number(totals.net_deposit) !== 0) ||
    Boolean(detail?.summary?.payout_range?.from || detail?.summary?.payout_range?.to)

  return totalsMatch && hasDetailData
}

export const resolvePayoutPlatformSync = (
  row: any,
  connectedPlatforms: any[] = [],
  routePlatformId?: string
): ResolvedPayoutPlatform | null => {
  if (routePlatformId) {
    const selectedPlatform = connectedPlatforms.find(
      (platform) => String(platform?.id) === String(routePlatformId)
    )
    return getPlatformIdentifiers(selectedPlatform)
  }

  const candidates = getCandidatePlatformsForRow(row, connectedPlatforms)
  if (candidates.length === 1) {
    return getPlatformIdentifiers(candidates[0])
  }

  return null
}

export const resolvePayoutPlatformAsync = async (
  row: any,
  connectedPlatforms: any[] = [],
  payoutDate: string,
  routePlatformId?: string
): Promise<ResolvedPayoutPlatform | null> => {
  const syncResolved = resolvePayoutPlatformSync(row, connectedPlatforms, routePlatformId)
  if (syncResolved) return syncResolved

  const candidates = getCandidatePlatformsForRow(row, connectedPlatforms)
  for (const platform of candidates) {
    try {
      const response: any = await payoutApiGet<PayoutDetailResponse>(
        `/api/platform-order-payout-details/${platform.id}/${encodeURIComponent(payoutDate)}`
      )
      const detail = response?.data || response
      if (isDetailMatchForRow(row, detail)) {
        return getPlatformIdentifiers(platform)
      }
    } catch {
      // Try the next candidate platform.
    }
  }

  if (candidates[0]) {
    return getPlatformIdentifiers(candidates[0])
  }

  return null
}

export const buildPayoutDetailPath = (
  resolved: ResolvedPayoutPlatform | null,
  payoutDate?: string | null
) => {
  if (!resolved?.connectedPlatformId || !resolved?.availablePlatformId || !payoutDate) {
    return ''
  }
  return `/payout-logs/${resolved.availablePlatformId}/${resolved.connectedPlatformId}/${payoutDate}`
}
